import {Pipe, PipeTransform} from '@angular/core';
import {combineLatest, Observable, of, timer} from 'rxjs';
import {catchError, map, shareReplay, startWith} from 'rxjs/operators';
import {Apollo} from 'apollo-angular';
import {Archive, File, Query} from '../types-repository';
import {FILE_ARCHIVE_STATE} from '../queries-repository';
import {restoreProgressOf, restoreWording} from '../restore-wording';

/**
 * How long the restore of an archived file will still take, in words ("in a few hours", VTK-3472):
 * S3 gives no real ETA, so no clock time. Use with async: ``{{ file | estimatedRestorationTime | async }}``.
 *
 * It returns an Observable because the restore's tier and start have to be fetched (a HEAD request behind
 * FILE_ARCHIVE_STATE), and a pure pipe that fills a cache asynchronously never shows the result: Angular
 * does not call it again for the same file, so it stayed at "Calculating..." for good (VTK-3469). The
 * fetch happens once per file; the wording is then recomputed every minute.
 */
@Pipe({
  name: 'estimatedRestorationTime',
})
export class EstimatedRestorationTimePipe implements PipeTransform {
  private readonly archives = new Map<string, Observable<Archive | null>>(); // file id -> archive state

  constructor(private apollo: Apollo) {
  }

  transform(file: File): Observable<string> {
    if (!file?.id) {
      return of(restoreWording([]));
    }
    return combineLatest([this.archive(file.id), timer(0, 60_000)]).pipe(
      map(([archive]) => restoreWording(archive ? [restoreProgressOf({...file, archive})] : [])),
      startWith('Calculating...'),
    );
  }

  private archive(fileId: string): Observable<Archive | null> {
    let archive = this.archives.get(fileId);
    if (!archive) {
      archive = this.apollo.query<Query>({query: FILE_ARCHIVE_STATE, variables: {id: fileId}}).pipe(
        map(d => d.data?.file?.archive ?? null),
        catchError(error => {
          console.error(`Failed to fetch the archive state of ${fileId}:`, error);
          return of(null);
        }),
        shareReplay(1),
      );
      this.archives.set(fileId, archive);
    }
    return archive;
  }
}
